import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import { Input, Container } from 'reactstrap';
import { FieldLabel, createUsePuck } from '@puckeditor/core';
import { withLayout } from './PuckLayout';
import formatIntl from './Common/formatIntl';
import ColorPickerField from './Common/ColorPickerField';
import RadioField from './Common/RadioField';

const usePuckSelector = createUsePuck();

const PRESETS = ['1', '6 6', '8 4', '4 4 4', '6 3 3', '3 6 3', '3 3 6', '3 3 3 3'];
const getPresetLabel = (preset) => {
  const labels = {
    1: formatIntl('Full'),
    '6 6': formatIntl('1/2 1/2'),
    '8 4': formatIntl('2/3 1/3'),
    '4 4 4': formatIntl('1/3 1/3 1/3'),
    '6 3 3': formatIntl('1/2 1/4 1/4'),
    '3 6 3': formatIntl('1/4 1/2 1/4'),
    '3 3 6': formatIntl('1/4 1/4 1/2'),
    '3 3 3 3': formatIntl('1/4 1/4 1/4 1/4'),
  };

  return labels[preset] ?? preset;
};

const SLOT_KEYS = [
  'items', 'itemsCol2', 'itemsCol3', 'itemsCol4', 'itemsCol5', 'itemsCol6',
  'itemsCol7', 'itemsCol8', 'itemsCol9', 'itemsCol10', 'itemsCol11', 'itemsCol12',
];

const parseColumns = (col) => {
  if (!col) return [12];
  const str = String(col).trim();

  if (str.includes(' ')) return str.split(/\s+/).map(Number);

  const count = parseInt(str);
  if (!Number.isNaN(count) && count > 0) {
    const span = Math.floor(12 / count);
    return Array(count).fill(span);
  }

  return [12];
};

const toGridTemplate = (col) => {
  const spans = parseColumns(col);
  const total = spans.reduce((a, b) => a + b, 0);
  return spans.map((s) => `${(s / total) * 100}fr`).join(' ');
};

const GridPresetRadioField = ({
  id,
  name,
  value,
  onChange,
  readOnly,
  field,
}) => (
  <div className="puck-grid-presets-field">
    <RadioField
      id={id}
      name={name}
      label={field.label}
      value={value || '6 6'}
      onChange={onChange}
      options={field.options}
      readOnly={readOnly}
    />
  </div>
);

GridPresetRadioField.propTypes = {
  id: PropTypes.string,
  name: PropTypes.string,
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  readOnly: PropTypes.bool,
  field: PropTypes.shape({
    label: PropTypes.string,
    options: PropTypes.array,
  }).isRequired,
};

GridPresetRadioField.defaultProps = {
  id: '',
  name: '',
  value: '6 6',
  readOnly: false,
};

const GapInput = ({ value, onChange, label }) => {
  const [localValue, setLocalValue] = React.useState(value);

  React.useEffect(() => {
    setLocalValue(value);
  }, [value]);

  const handleChange = (e) => {
    const raw = e.currentTarget.value;
    setLocalValue(raw);
    if (raw !== '' && !Number.isNaN(parseFloat(raw))) {
      onChange(raw);
    }
  };

  return (
    <FieldLabel label={label} className="puck-grid-gap-field-label">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginTop: 4,
      }}
      >
        <Input
          type="number"
          min="0"
          step="0.25"
          value={localValue}
          onChange={handleChange}
        />
        <span style={{ color: '#888', fontSize: 12 }}>rem</span>
      </div>
    </FieldLabel>
  );
};

GapInput.propTypes = {
  value: PropTypes.string.isRequired,
  onChange: PropTypes.func.isRequired,
  label: PropTypes.string.isRequired,
};

const BorderColorField = ({ value, onChange, field }) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const borderEnabled = selectedItem?.props?.border === 'enabled';

  if (!borderEnabled) {
    return <div className="puck-grid-border-color-hidden" />;
  }

  return (
    <FieldLabel label={field.label} className="puck-grid-border-color-field-label">
      <ColorPickerField
        value={value || '#dfe3ea'}
        onChange={onChange}
        showLabel={false}
        className="puck-grid-border-color-row"
        controlsClassName="puck-grid-border-color-controls"
        buttonClassName="puck-grid-border-color-btn"
        inputClassName="puck-grid-border-color-input"
        popoverClassName="puck-grid-border-color-picker-popover"
        defaultColor="#dfe3ea"
      />
    </FieldLabel>
  );
};

BorderColorField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

BorderColorField.defaultProps = {
  value: '#dfe3ea',
};

const BorderRadiusField = ({ value, onChange, field }) => {
  const selectedItem = usePuckSelector((s) => s.selectedItem);
  const borderEnabled = selectedItem?.props?.border === 'enabled';

  if (!borderEnabled) {
    return <div className="puck-grid-border-radius-hidden" />;
  }

  return (
    <FieldLabel label={field.label} className="puck-grid-border-radius-field-label">
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginTop: 4,
      }}
      >
        <Input
          type="number"
          min="0"
          step="1"
          value={value || '0'}
          onChange={e => onChange(e.currentTarget.value)}
        />
        <span style={{ color: '#888', fontSize: 12 }}>px</span>
      </div>
    </FieldLabel>
  );
};

BorderRadiusField.propTypes = {
  value: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  field: PropTypes.shape({
    label: PropTypes.string,
  }).isRequired,
};

BorderRadiusField.defaultProps = {
  value: '0',
};

const ColumnRuler = ({ columns, gap }) => {
  const spans = parseColumns(columns);
  const total = spans.reduce((a, b) => a + b, 0);
  const rulerGap = Math.max(6, parseFloat(gap) * 16);
  const segments = spans.reduce((acc, span) => {
    const count = acc.filter((s) => s.span === span).length;
    return [...acc, { id: `${span}-${count}`, span }];
  }, []);

  return (
    <div style={{
      display: 'flex', width: '100%', height: 12, marginBottom: 4, gap: rulerGap,
    }}
    >
      {segments.map(({ id, span }) => (
        <div
          key={id}
          style={{
            flex: span / total,
            background: 'rgba(79, 142, 247, 0.25)',
            border: '1px solid rgba(79, 142, 247, 0.5)',
            borderRadius: 2,
          }}
        />
      ))}
    </div>
  );
};

ColumnRuler.propTypes = {
  columns: PropTypes.string.isRequired,
  gap: PropTypes.string.isRequired,
};

const PuckGridRender = ({
  columns = '6 6',
  rows = '',
  gap = '1',
  verticalPadding = '0',
  border = 'disabled',
  borderColor = '#dfe3ea',
  borderRadius = '0',
  container,
  puck,
  items,
  itemsCol2,
  itemsCol3,
  itemsCol4,
  itemsCol5,
  itemsCol6,
  itemsCol7,
  itemsCol8,
  itemsCol9,
  itemsCol10,
  itemsCol11,
  itemsCol12,
}) => {
  const spans = parseColumns(columns);
  const borderEnabled = border === 'enabled';
  const gapValue = parseFloat(gap) || 0;
  const verticalPaddingValue = parseFloat(verticalPadding) || 0;
  const collapseInnerBorders = borderEnabled && gapValue === 0;
  const gridClassName = classnames('puck-grid', {
    'puck-grid-with-border': borderEnabled,
    'puck-grid-no-border': !borderEnabled,
  });

  const slots = [
    items, itemsCol2, itemsCol3, itemsCol4, itemsCol5, itemsCol6,
    itemsCol7, itemsCol8, itemsCol9, itemsCol10, itemsCol11, itemsCol12,
  ];

  const gridContent = (
    <>
      {puck?.isEditing && <ColumnRuler columns={columns} gap={gap} />}
      <div
        className={gridClassName}
        style={{
          display: 'grid',
          gridTemplateColumns: toGridTemplate(columns),
          ...(rows ? { gridTemplateRows: toGridTemplate(rows) } : {}),
          gap: `${gapValue}rem`,
          width: '100%',
          boxSizing: 'border-box',
        }}
      >
        {spans.map((span, index) => {
          const Slot = slots[index];
          if (!Slot) return null;

          return (
            <div
              key={SLOT_KEYS[index]}
              className={classnames('puck-grid-cell', {
                'puck-grid-cell-with-border': borderEnabled,
                'puck-grid-cell-no-border': !borderEnabled,
              })}
              style={{
                minWidth: 0,
                paddingTop: `${verticalPaddingValue}rem`,
                paddingBottom: `${verticalPaddingValue}rem`,
                ...(borderEnabled
                  ? {
                    border: `1px solid ${borderColor || '#dfe3ea'}`,
                    ...(collapseInnerBorders && index > 0 ? { borderLeftWidth: 0 } : {}),
                    borderRadius: `${parseFloat(borderRadius) || 0}px`,
                  }
                  : {}),
              }}
            >
              <Slot minEmptyHeight={puck?.isEditing ? 40 : undefined} />
            </div>
          );
        })}
      </div>
    </>
  );

  if (container === 'container') {
    return (
      <Container>
        {gridContent}
      </Container>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {gridContent}
    </div>
  );
};

PuckGridRender.propTypes = {
  columns: PropTypes.string,
  rows: PropTypes.string,
  gap: PropTypes.string,
  verticalPadding: PropTypes.string,
  border: PropTypes.string,
  borderColor: PropTypes.string,
  borderRadius: PropTypes.string,
  container: PropTypes.string,
  puck: PropTypes.shape({
    isEditing: PropTypes.bool,
    setProps: PropTypes.func,
  }).isRequired,
  items: PropTypes.func,
  itemsCol2: PropTypes.func,
  itemsCol3: PropTypes.func,
  itemsCol4: PropTypes.func,
  itemsCol5: PropTypes.func,
  itemsCol6: PropTypes.func,
  itemsCol7: PropTypes.func,
  itemsCol8: PropTypes.func,
  itemsCol9: PropTypes.func,
  itemsCol10: PropTypes.func,
  itemsCol11: PropTypes.func,
  itemsCol12: PropTypes.func,
};

PuckGridRender.defaultProps = {
  columns: '6 6',
  rows: '',
  gap: '1',
  verticalPadding: '0',
  border: 'disabled',
  borderColor: '#dfe3ea',
  borderRadius: '0',
  container: 'container',
  items: undefined,
  itemsCol2: undefined,
  itemsCol3: undefined,
  itemsCol4: undefined,
  itemsCol5: undefined,
  itemsCol6: undefined,
  itemsCol7: undefined,
  itemsCol8: undefined,
  itemsCol9: undefined,
  itemsCol10: undefined,
  itemsCol11: undefined,
  itemsCol12: undefined,
};

const PuckGridConfig = {
  get label() { return formatIntl('Grid'); },

  fields: {
    columns: {
      type: 'custom',
      get label() { return formatIntl('Columns'); },
      options: PRESETS.map((preset) => ({
        label: getPresetLabel(preset),
        value: preset,
      })),
      render: (props) => <GridPresetRadioField {...props} />,
    },
    gap: {
      type: 'custom',
      get label() { return formatIntl('Gap'); },
      render: ({ value, onChange, field }) => (
        <GapInput value={value || '1'} onChange={onChange} label={field.label} />
      ),
    },
    verticalPadding: {
      type: 'custom',
      get label() { return formatIntl('Vertical padding'); },
      render: ({ value, onChange, field }) => (
        <GapInput value={value || '0'} onChange={onChange} label={field.label} />
      ),
    },
    border: {
      type: 'radio',
      get label() { return formatIntl('Border'); },
      options: [
        { get label() { return formatIntl('Enable'); }, value: 'enabled' },
        { get label() { return formatIntl('Disable'); }, value: 'disabled' },
      ],
    },
    borderColor: {
      type: 'custom',
      get label() { return formatIntl('Border color'); },
      render: (props) => <BorderColorField {...props} />,
    },
    borderRadius: {
      type: 'custom',
      get label() { return formatIntl('Border radius'); },
      render: (props) => <BorderRadiusField {...props} />,
    },
    container: {
      type: 'radio',
      get label() { return formatIntl('Width'); },
      options: [
        { get label() { return formatIntl('Container'); }, value: 'container' },
        { get label() { return formatIntl('Full width'); }, value: 'full' },
      ],
    },
    items: { type: 'slot' },
    itemsCol2: { type: 'slot' },
    itemsCol3: { type: 'slot' },
    itemsCol4: { type: 'slot' },
    itemsCol5: { type: 'slot' },
    itemsCol6: { type: 'slot' },
    itemsCol7: { type: 'slot' },
    itemsCol8: { type: 'slot' },
    itemsCol9: { type: 'slot' },
    itemsCol10: { type: 'slot' },
    itemsCol11: { type: 'slot' },
    itemsCol12: { type: 'slot' },
  },

  defaultProps: {
    columns: '6 6',
    rows: '',
    gap: '1',
    verticalPadding: '0',
    border: 'disabled',
    borderColor: '#dfe3ea',
    borderRadius: '0',
    container: 'container',
  },

  // eslint-disable-next-line react/jsx-props-no-spreading
  render: (props) => <PuckGridRender {...props} />,
};

export default withLayout(PuckGridConfig);
